/**
 * Shared wizard chrome (027 US6): the pinned top-bar, the scrollable step
 * area, the pinned bottom action bar with progress-dots, and the step/card
 * class vocabulary from visual-map.md §1 (step-headline, card-choice,
 * bottom-bar, progress-dots idioms). Extracted so `ComposeWizard` (the shell +
 * step-0 chain) and `WizardQuestionFlow` (the per-field question steps, PR-2)
 * compose the same chrome without either file crossing the 400-line ceiling.
 * Token-backed classes only — no raw hex.
 *
 * Layout contract (the owner fold at PR-3): the wizard dialog
 * (`Dialog.Content variant="wizard"`) is a fixed-height flex column that does
 * NOT scroll. Consumers compose, in order, a `WizardTopBar`, ONE
 * `WizardScrollArea` holding the step content, and (where the step has an
 * action) a `WizardBottomBar` — the bars are static `shrink-0` flex children
 * pinned outside the scroll area, so step content (and the calendar popover)
 * can never hide behind or overlap them.
 */

import { type JSX, type ReactNode, type Ref, useEffect, useRef } from 'react';

import { useTranslate } from '../../../i18n/useTranslate.js';
import { Button } from '../../../primitives/Button.js';
import { BackArrowIcon, CloseIcon } from '../../../shell/icons.js';

// `m-auto` (BOTH axes) is the overflow-safe stage-centering idiom (owner
// finding #4): inside the flex-col scroll area a short step's auto margins
// absorb the free space (vertical centering), while a tall step's margins
// collapse to zero and the content scrolls top-anchored as normal.
export const entryColumnClasses = 'm-auto flex w-full max-w-2xl flex-col gap-5 text-center';
export const entryListClasses = 'mx-auto flex w-full max-w-xl flex-col gap-4 px-4 text-left';
export const stepColumnClasses = 'm-auto flex w-full max-w-xl flex-col gap-4 px-4 py-6 text-center';
export const stepLeadClasses = 'm-0 text-base font-semibold text-foreground';
export const choiceListClasses = 'flex flex-col gap-3 text-left';
export const choiceCardClasses =
  'flex w-full items-center gap-4 rounded-xl border-2 border-border bg-card p-4 text-left ' +
  'transition-colors hover:border-primary/50 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
// visual-map card-choice SELECTED treatment (#619C38 border + /10 tint via the
// primary token), shown when the member backs onto an answered choice step. A
// full sibling variant, NOT an appended override: `cn()` is plain concat, so a
// competing same-property class would resolve by Tailwind compile order.
export const choiceCardSelectedClasses =
  'flex w-full items-center gap-4 rounded-xl border-2 border-primary bg-primary/10 p-4 text-left ' +
  'transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
export const choiceTitleClasses = 'm-0 block text-sm font-semibold text-foreground md:text-base';
// Notice-card idiom (the row-19 intro slot and the row-12-amended PREFIX
// start-of-flow slot share it): bordered muted card, pre-line for any
// authored line breaks.
export const stepNoticeWrapClasses = 'mx-auto w-full max-w-xl px-4 pt-6 text-left';
export const stepNoticeClasses =
  'm-0 whitespace-pre-line rounded-md border border-border bg-muted p-4 text-sm text-muted-foreground';

const topBarClasses =
  'flex shrink-0 items-center justify-between rounded-t-none bg-popover px-5 py-4 md:rounded-t-lg';
const ghostIconButtonClasses =
  'inline-flex h-10 w-10 items-center justify-center rounded-full text-brand-teal ' +
  'transition-colors hover:bg-brand-teal hover:text-brand-teal-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const iconClasses = 'h-4 w-4';

const bottomBarClasses =
  'flex shrink-0 flex-col gap-3 border-t border-border bg-background px-5 py-4 md:rounded-b-lg';
const dotsRowClasses = 'flex justify-center gap-2';
const dotClasses = 'h-2.5 w-2.5 rounded-full';

export interface WizardScrollAreaProps {
  readonly children: ReactNode;
  readonly variant?: 'default' | 'choice' | 'call';
  /** Imperative scroll-to-top seam: the owner resets position on step changes. */
  readonly ref?: Ref<HTMLDivElement>;
}

/**
 * The ONE scrolling region of the wizard layout (`flex-1 min-h-0
 * overflow-y-auto` between the pinned bars). `data-wizard-scroll` is the
 * stable structural marker the scroll-reset wiring and the no-clip regression
 * guards anchor on.
 *
 * `flex flex-col` makes the step column's `m-auto` center SHORT steps
 * vertically in the stage (owner finding #4). Deliberately NO
 * `items-center`/`justify-center` here: alignment on an overflow container
 * clips the TOP of taller-than-stage content unreachably (the long
 * Lakikaverille topic-list step would hit it) — auto margins on the child
 * degrade to top-anchored scrolling instead. Do not "simplify" this back.
 */
export function WizardScrollArea({
  children,
  ref,
  variant = 'default',
}: WizardScrollAreaProps): JSX.Element {
  const padding =
    variant === 'choice'
      ? 'px-5 py-4 pb-28'
      : variant === 'call'
        ? 'px-5 pt-4 pb-wizard-call-bottom sm:pb-wizard-call-bottom-wide'
        : '';
  return (
    <div
      ref={ref}
      data-wizard-scroll=""
      className={`flex min-h-0 flex-1 flex-col overflow-y-auto ${padding}`}
    >
      {children}
    </div>
  );
}

export interface WizardTopBarProps {
  /** Back arrow renders only when a back rule exists for the current step. */
  readonly onBack?: (() => void) | undefined;
  readonly onClose: () => void;
  /**
   * When set, close is temporarily unavailable: the X is `aria-disabled` and
   * described by the element with this id, which says why.
   */
  readonly closeUnavailableDescribedBy?: string | undefined;
}

/** top-bar idiom: ghost back/close over the scrollable step content. */
export function WizardTopBar({
  onBack,
  onClose,
  closeUnavailableDescribedBy,
}: WizardTopBarProps): JSX.Element {
  const t = useTranslate();
  return (
    <div className={topBarClasses}>
      {onBack !== undefined ? (
        <button
          type="button"
          aria-label={t('compose.wizard.back')}
          className={ghostIconButtonClasses}
          onClick={onBack}
        >
          <BackArrowIcon aria-hidden="true" className={iconClasses} />
        </button>
      ) : (
        <span aria-hidden="true" className="h-10 w-10" />
      )}
      <button
        type="button"
        aria-label={t('compose.wizard.close')}
        {...(closeUnavailableDescribedBy === undefined
          ? {}
          : { 'aria-disabled': true, 'aria-describedby': closeUnavailableDescribedBy })}
        className={ghostIconButtonClasses}
        onClick={onClose}
      >
        <CloseIcon aria-hidden="true" className={iconClasses} />
      </button>
    </div>
  );
}

export interface ProgressDotsState {
  /** Total question steps on the ACTIVE path (fields + the attachment step). */
  readonly total: number;
  /** Zero-based position of the current step. */
  readonly current: number;
}

export interface WizardBottomBarProps {
  readonly label: string;
  readonly disabled?: boolean;
  readonly onClick: () => void;
  /** progress-dots idiom: rendered above the action on question steps only. */
  readonly dots?: ProgressDotsState | undefined;
  /**
   * Focus the action button on mount (owner UX nit r2): the terminal-shaped
   * bars (Lähetä on the confirmation overview, Sulje on the thank-you
   * terminal) take focus so a discrete Enter completes the keyboard journey.
   * Pair with a distinct `key` so entering the phase actually remounts the
   * bar — React reconciles same-type footers in place otherwise. Held-Enter
   * autorepeat is suppressed at the wizard shell.
   */
  readonly focusOnMount?: boolean;
}

/** bottom-bar idiom: the sticky primary action (+ optional progress dots). */
export function WizardBottomBar({
  label,
  disabled = false,
  onClick,
  dots,
  focusOnMount = false,
}: WizardBottomBarProps): JSX.Element {
  const actionRef = useRef<HTMLButtonElement>(null);
  // R35: focus is a DOM side effect of the bar mounting (focusOnMount is
  // fixed per bar instance; the dep just satisfies exhaustive-deps).
  useEffect(() => {
    if (focusOnMount) {
      actionRef.current?.focus();
    }
  }, [focusOnMount]);
  return (
    <div className={bottomBarClasses}>
      {dots !== undefined ? (
        <div className={dotsRowClasses} aria-hidden="true">
          {Array.from({ length: dots.total }, (_, index) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: the dots are a fixed positional row with no identity beyond the index
              key={index}
              className={`${dotClasses} ${index <= dots.current ? 'bg-primary' : 'bg-muted'}`}
            />
          ))}
        </div>
      ) : null}
      <Button
        ref={actionRef}
        type="button"
        variant="primary"
        className="w-full shadow-lg sm:min-h-12 sm:text-lg"
        disabled={disabled}
        onClick={onClick}
      >
        {label}
      </Button>
    </div>
  );
}
